import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { utf8_to_b64 } from "@commonUtils"
import { keysToCamel } from "@lib/snakeTocamel"
import { parseResponseData } from "./parser"
import { handleAuthentication } from "@containers/App/reducer"
import { CONSTANTS } from "./constant"

const initialState = {
    data: null,
    error: null,
    isLoading: false,
    isFetched: false,
    cashlessBenefit: {
        data: null,
        isFetched: false,
        isFetching: false,
        error: null,
    },
    authenticateEkinCare: {
        error: null,
        data: null,
    },
}

/**
 * corporate dashboard reducer
 */

export const corporateDashboardSlice = createSlice({
    name: "corporateDashboardReducer",
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        //handle asynchronous actions for corporate dashboard
        builder.addCase(fetchCorporateDashboardData.fulfilled, (state, action) => {
            state.isLoading = false
            state.isFetched = true
            state.error = null
            state.data = utf8_to_b64(action.payload)
        }),
            builder.addCase(fetchCorporateDashboardData.pending, (state) => {
                state.isLoading = true
            }),
            builder.addCase(fetchCorporateDashboardData.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })
        //handle asynchronous actions for cashless benefits
        builder.addCase(fetchCashlessBenefitData.fulfilled, (state, action) => {
            state.cashlessBenefit.isLoading = false
            state.cashlessBenefit.isFetched = true
            state.cashlessBenefit.error = null
            state.cashlessBenefit.data = parseResponseData(keysToCamel(action.payload))
        }),
            builder.addCase(fetchCashlessBenefitData.pending, (state) => {
                state.cashlessBenefit.isLoading = true
                state.cashlessBenefit.isFetched = false
            }),
            builder.addCase(fetchCashlessBenefitData.rejected, (state, action) => {
                state.cashlessBenefit.isLoading = false
                state.cashlessBenefit.isFetched = true
                state.cashlessBenefit.error = action.payload
            })

        builder.addCase(authenticateUserWithEkincare.fulfilled, (state, action) => {
            state.authenticateEkinCare.error = null
            state.authenticateEkinCare.data = action.payload
        }),
            builder.addCase(authenticateUserWithEkincare.pending, (state) => {
                state.authenticateEkinCare.error = null
            }),
            builder.addCase(authenticateUserWithEkincare.rejected, (state, action) => {
                state.authenticateEkinCare.error = action.payload
                state.authenticateEkinCare.data = null
            })
    },
})

/**
 * fetch corporate dashboard data
 */

export const fetchCorporateDashboardData = createAsyncThunk(
    "corporateDashboard/fetchData",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { api } = extra
        const { slug } = _arg
        const onServer = typeof window === "undefined"
        const { loggedIn } = getState().shellReducer
        try {
            const res = await api.get(`/webservices/static-content/corporate`, {
                headers: {
                    slug: slug,
                    "X-HTML-CanRender": "True",
                },
            })
            if (!loggedIn) {
                dispatch(handleAuthentication({ restrictUserFromClosingAuth: true }))
                return
            } else {
                return Promise.resolve(res.html_text)
            }
        } catch (err) {
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication({ restrictUserFromClosingAuth: true }))
            return rejectWithValue(err || err.error.message)
        }
    }
)

/**
 * fetch cashless benefits data
 */

export const fetchCashlessBenefitData = createAsyncThunk(
    "cashlessBenefitData/fetchData",
    async (_arg, { extra, rejectWithValue, getState }) => {
        const { api } = extra
        const { corporateDetails } = getState().shellReducer
        try {
            const res = await api.get(
                `/api/corporate/${corporateDetails?._source}/beneficiary/cashless/details`
            )
            return Promise.resolve(res.data)
        } catch (err) {
            return rejectWithValue(err.message || err.error.message)
        }
    }
)

/**
 * authenticate user for Ekincare platform
 */

export const authenticateUserWithEkincare = createAsyncThunk(
    "Ekincare/authenticateData",
    async (_arg, { extra, rejectWithValue, getState }) => {
        const { api } = extra
        const { corporateDetails } = getState().shellReducer
        const source = corporateDetails?._source
        try {
            const res = await api.get(`/api/ekincare/${source}/generate-redirect-tag`)
            return Promise.resolve(res?.data)
        } catch (error) {
            return rejectWithValue(CONSTANTS.RETRY_ERROR)
        }
    }
)

export const corporateDashboardReducer = corporateDashboardSlice.reducer
